import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from './context';
import { loginPathWithNext } from './nextPath';
import { hasGuestSession } from './guest';
import { fetchSharePath } from '../share/api';

/**
 * Route guard for surfaces a guest (anonymous access) may see: their trip
 * detail and journey detail pages. Admits a signed-in user OR a device with
 * a guest session; everyone else goes to login with `?next=` preserved
 * (same contract as RequireAuth, minus the toast — a shared link landing
 * here is normal traffic, not an auth error).
 *
 * `sharedFallback` (the /trips/:id and /journeys/:id routes): before sending
 * a signed-out visitor to login, check whether the trip behind `:id` is
 * shared, and if so open its /s/ page instead (Paulo 2026-10-03: an owner may
 * paste their own app URL to a friend). Signed-in non-owners get the same
 * redirect from the pages' 404 handling. Not for `?share=1`: that is the
 * owner's "Manage sharing" link from the trip-shared email, so a signed-out
 * owner goes to login and comes back to their trip, as before.
 */
const RequireAuthOrGuest: React.FC<{ children: React.ReactNode; sharedFallback?: 'trip' | 'journey' }> = ({ children, sharedFallback }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const signedOut = !loading && !user && !hasGuestSession();
  const ownerLink = new URLSearchParams(location.search).get('share') === '1';
  const lookupId = signedOut && sharedFallback && id && !ownerLink ? id : null;
  // Keyed by id so a result never applies to another trip's URL.
  const [lookup, setLookup] = useState<{ id: string; path: string | null } | null>(null);

  useEffect(() => {
    if (!lookupId) return;
    let alive = true;
    fetchSharePath(sharedFallback === 'trip' ? { tripId: lookupId } : { journeyId: lookupId })
      .then((path) => { if (alive) setLookup({ id: lookupId, path }); });
    return () => { alive = false; };
  }, [lookupId, sharedFallback]);

  if (loading) return null;
  if (signedOut) {
    if (lookupId) {
      if (!lookup || lookup.id !== lookupId) return null;
      if (lookup.path) return <Navigate to={lookup.path + location.search} replace />;
    }
    return <Navigate to={loginPathWithNext(location)} replace />;
  }
  return <>{children}</>;
};

export default RequireAuthOrGuest;
